/**
 * LQD plan catalog -- the single source of truth for plan names, prices,
 * seat limits and feature entitlements. Client-safe: no server imports.
 *
 * Marketing (welcome page, /pricing) and the in-app plan picker both read
 * from here; Stripe price ids are mapped in `src/billing/prices.ts`.
 */

export const PLAN_IDS = ['free', 'splash', 'flow', 'wave'] as const;
export type PlanId = (typeof PLAN_IDS)[number];

export const BILLING_INTERVALS = ['month', 'year'] as const;
export type BillingInterval = (typeof BILLING_INTERVALS)[number];

export const FEATURES = {
  ClassicRecipes: 'classic_recipes',
  HouseRecipes: 'house_recipes',
  Menus: 'menus',
  EightySix: 'eighty_six',
  Messages: 'messages',
  Team: 'team',
  Logbook: 'logbook',
  AiMenuImport: 'ai_menu_import',
  CustomReports: 'custom_reports',
  AiImages: 'ai_images'
} as const;

export type FeatureId = (typeof FEATURES)[keyof typeof FEATURES];

export type SupportLevel = 'none' | 'docs' | 'email' | 'priority';

export type Plan = {
  id: PlanId;
  /** Brand name, never translated. */
  name: string;
  /** English marketing tagline; the app uses `billing.plans.<id>.tagline`. */
  tagline: string;
  /** USD per month when billed monthly. `null` = contact sales. */
  monthlyPrice: number | null;
  /** USD per month when billed yearly. `null` = no yearly price. */
  yearlyMonthlyPrice: number | null;
  /** Distinct members allowed in the organization. `null` = unlimited. */
  seatLimit: number | null;
  /** AI images generatable per calendar month. `null` = unlimited. */
  imageGenerationLimit: number | null;
  features: readonly FeatureId[];
  support: SupportLevel;
  /** Purchasable through Stripe Checkout from the app. */
  selfServe: boolean;
  /** Shown on marketing pages and in the plan picker. */
  publiclyAvailable: boolean;
};

const SPLASH_FEATURES: readonly FeatureId[] = [
  FEATURES.ClassicRecipes,
  FEATURES.HouseRecipes,
  FEATURES.Menus,
  FEATURES.EightySix,
  FEATURES.Messages,
  FEATURES.Team
];

const FLOW_FEATURES: readonly FeatureId[] = [
  ...SPLASH_FEATURES,
  FEATURES.Logbook,
  FEATURES.AiMenuImport,
  FEATURES.AiImages
];

const WAVE_FEATURES: readonly FeatureId[] = [
  ...FLOW_FEATURES,
  FEATURES.CustomReports
];

export const PLANS: Record<PlanId, Plan> = {
  free: {
    id: 'free',
    name: 'Free',
    tagline: 'Browse the classic cocktail library.',
    monthlyPrice: 0,
    yearlyMonthlyPrice: 0,
    seatLimit: 1,
    imageGenerationLimit: 0,
    features: [FEATURES.ClassicRecipes],
    support: 'none',
    selfServe: false,
    publiclyAvailable: false
  },
  splash: {
    id: 'splash',
    name: 'Splash',
    tagline:
      'Perfect for small teams dipping their toes into organized management.',
    monthlyPrice: 59,
    yearlyMonthlyPrice: 49,
    seatLimit: 5,
    imageGenerationLimit: 0,
    features: SPLASH_FEATURES,
    support: 'docs',
    selfServe: true,
    publiclyAvailable: true
  },
  flow: {
    id: 'flow',
    name: 'Flow',
    tagline:
      'Designed for bustling bars and restaurants that need full control.',
    monthlyPrice: 195,
    yearlyMonthlyPrice: 175,
    seatLimit: 100,
    imageGenerationLimit: 200,
    features: FLOW_FEATURES,
    support: 'email',
    selfServe: true,
    publiclyAvailable: true
  },
  wave: {
    id: 'wave',
    name: 'Wave',
    tagline:
      'The ultimate solution for brands that need a fully customized experience.',
    monthlyPrice: null,
    yearlyMonthlyPrice: null,
    seatLimit: null,
    imageGenerationLimit: null,
    features: WAVE_FEATURES,
    support: 'priority',
    selfServe: false,
    publiclyAvailable: true
  }
};

/** Least to most capable. */
export const PLAN_ORDER: readonly PlanId[] = ['free', 'splash', 'flow', 'wave'];

/** Plans that appear on marketing pages and in the in-app picker. */
export const PUBLIC_PLANS: readonly Plan[] = PLAN_ORDER.map(
  id => PLANS[id]
).filter(plan => plan.publiclyAvailable);

/** The plan an organization gets while on an app-level (no card) trial. */
export const TRIAL_PLAN: PlanId = 'flow';
export const TRIAL_DAYS = 14;

export function isPlanId(value: unknown): value is PlanId {
  return (
    typeof value === 'string' && (PLAN_IDS as readonly string[]).includes(value)
  );
}

export function planHasFeature(plan: PlanId, feature: FeatureId): boolean {
  return PLANS[plan].features.includes(feature);
}

export function comparePlans(a: PlanId, b: PlanId): number {
  return PLAN_ORDER.indexOf(a) - PLAN_ORDER.indexOf(b);
}

/** Percentage saved per month by paying yearly, rounded; 0 when no discount. */
export function yearlySavingsPercent(plan: Plan): number {
  if (!plan.monthlyPrice || plan.yearlyMonthlyPrice == null) return 0;
  return Math.round((1 - plan.yearlyMonthlyPrice / plan.monthlyPrice) * 100);
}

/** Total charged per year when billed yearly. */
export function yearlyTotal(plan: Plan): number | null {
  return plan.yearlyMonthlyPrice == null ? null : plan.yearlyMonthlyPrice * 12;
}

export function monthlyPriceFor(
  plan: Plan,
  interval: BillingInterval
): number | null {
  return interval === 'year' ? plan.yearlyMonthlyPrice : plan.monthlyPrice;
}

/**
 * Rows of the public feature comparison table (English marketing copy). The
 * in-app picker translates the same ids via `billing.features.<id>`.
 */
export type FeatureRow = {
  id: FeatureId | 'seats' | 'support';
  label: string;
  /** Value per public plan id; boolean renders check/cross, string renders text. */
  values: Partial<Record<PlanId, boolean | string>>;
};

function featureAvailability(
  feature: FeatureId
): Partial<Record<PlanId, boolean>> {
  return Object.fromEntries(
    PUBLIC_PLANS.map(plan => [plan.id, plan.features.includes(feature)])
  );
}

const SUPPORT_LABELS: Record<SupportLevel, string> = {
  none: 'None',
  docs: 'Online documentation',
  email: 'Email',
  priority: 'Priority'
};

export const FEATURE_ROWS: readonly FeatureRow[] = [
  {
    id: 'seats',
    label: 'Team members',
    values: Object.fromEntries(
      PUBLIC_PLANS.map(plan => [
        plan.id,
        plan.seatLimit == null ? 'Unlimited' : `Up to ${plan.seatLimit}`
      ])
    )
  },
  {
    id: FEATURES.ClassicRecipes,
    label: 'Classic cocktail library',
    values: featureAvailability(FEATURES.ClassicRecipes)
  },
  {
    id: FEATURES.HouseRecipes,
    label: 'House recipes',
    values: featureAvailability(FEATURES.HouseRecipes)
  },
  {
    id: FEATURES.Menus,
    label: 'Beverage & food menus',
    values: featureAvailability(FEATURES.Menus)
  },
  {
    id: FEATURES.EightySix,
    label: '86 list management',
    values: featureAvailability(FEATURES.EightySix)
  },
  {
    id: FEATURES.Messages,
    label: 'Team chat & announcements',
    values: featureAvailability(FEATURES.Messages)
  },
  {
    id: FEATURES.Logbook,
    label: "Manager's logbook",
    values: featureAvailability(FEATURES.Logbook)
  },
  {
    id: FEATURES.AiMenuImport,
    label: 'AI menu import (photo, PDF, DOCX)',
    values: featureAvailability(FEATURES.AiMenuImport)
  },
  {
    id: FEATURES.AiImages,
    label: 'AI-generated images',
    values: featureAvailability(FEATURES.AiImages)
  },
  {
    id: FEATURES.CustomReports,
    label: 'Custom reports & features',
    values: featureAvailability(FEATURES.CustomReports)
  },
  {
    id: 'support',
    label: 'Support',
    values: Object.fromEntries(
      PUBLIC_PLANS.map(plan => [plan.id, SUPPORT_LABELS[plan.support]])
    )
  }
];
